import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Paperclip, Reply, Loader2, MessageSquare } from 'lucide-react';
import { formatDateTime, formatRelative } from '../../utils/dateUtils';
import { getThreadEmails } from '../../services/mailService';

// CSS for styling quoted content in HTML email iframes
const QUOTE_STYLES = `
blockquote, .gmail_quote, .yahoo_quoted, .moz-cite-prefix + blockquote,
div[class*="quote"], div[class*="Reply"], div[class*="reply"] {
  border-left: 3px solid #cbd5e1 !important;
  padding-left: 12px !important;
  margin: 12px 0 !important;
  color: #64748b !important;
}
.gmail_attr, .gmail_quote > div:first-child,
div[class*="wrote"], .moz-cite-prefix {
  color: #94a3b8 !important;
  font-size: 12px !important;
  margin-bottom: 4px !important;
}
blockquote blockquote, .gmail_quote .gmail_quote {
  border-left-color: #e2e8f0 !important;
  color: #94a3b8 !important;
}
`;

// Strip quoted content from thread messages to avoid redundant display
function stripQuotedContent(text) {
  if (!text) return text;
  const lines = text.split('\n');
  const cutPatterns = [
    /^-{3,}\s*(Original|Forwarded)\s*(Message|message)\s*-{3,}/,
    /^_{3,}\s*$/,
    /^On\s+.{10,}\s+wrote:\s*$/,
    /^On\s+.{10,}\s+<.+@.+>\s+wrote:\s*$/,
  ];
  
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    for (const pattern of cutPatterns) {
      if (pattern.test(trimmed)) {
        const original = lines.slice(0, i).join('\n').replace(/\n{3,}$/g, '\n');
        return original.trim() ? original : text;
      }
    }
    // Block of consecutive > lines
    if (/^>/.test(trimmed) && i > 0) {
      let quoteCount = 0;
      for (let j = i; j < Math.min(i + 5, lines.length); j++) {
        if (/^>/.test(lines[j].trim()) || lines[j].trim() === '') quoteCount++;
      }
      if (quoteCount >= 3) {
        const original = lines.slice(0, i).join('\n').replace(/\n{3,}$/g, '\n');
        return original.trim() ? original : text;
      }
    }
  }
  return text;
}

// Strip quoted HTML content (gmail_quote, blockquote after certain patterns)
function stripQuotedHtml(html) {
  if (!html) return html;
  // Remove gmail quote blocks
  let stripped = html.replace(/<div\s+class="gmail_quote"[\s\S]*$/i, '');
  // Remove trailing blockquotes
  stripped = stripped.replace(/<blockquote[\s\S]*$/i, stripped);
  // If we stripped too much (empty result), return original
  if (stripped.replace(/<[^>]*>/g, '').trim().length < 10) return html;
  return stripped;
}

const ThreadMessage = ({ message, isCurrentEmail, isExpanded, onToggle, isInThread }) => {
  const bodyRef = useRef(null);
  const isHtml = message.content?.includes('<') && message.content?.includes('>');

  // In thread view, strip quoted content to avoid redundancy since each message is shown separately
  const displayContent = isInThread
    ? (isHtml ? stripQuotedHtml(message.content) : stripQuotedContent(message.content))
    : message.content;

  return (
    <div className={`border rounded-lg transition-all ${
      isCurrentEmail
        ? 'border-cyan-500/40 bg-cyan-500/5'
        : 'border-slate-700/50 hover:border-slate-600'
    }`}>
      {/* Message header — always visible */}
      <button onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left group">
        <div className="flex-shrink-0 text-slate-500">
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{message.from || 'Unknown'}</span>
            {isCurrentEmail && (
              <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-300 rounded text-[10px] font-medium flex-shrink-0">
                Current
              </span>
            )}
            {!message.is_read && !isCurrentEmail && (
              <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0" />
            )}
          </div>
          {!isExpanded && (
            <p className="text-xs text-slate-500 truncate mt-0.5">
              {message.subject}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {message.has_attachments && <Paperclip className="w-3.5 h-3.5 text-slate-500" />}
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {formatRelative(message.received_at || message.sent_at)}
          </span>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-700/30">
          {/* To/CC line */}
          <div className="py-2 text-xs text-slate-500 space-y-1">
            <div><span className="text-slate-600">To:</span> {message.to}</div>
            {message.cc && <div><span className="text-slate-600">Cc:</span> {message.cc}</div>}
            <div><span className="text-slate-600">Date:</span> {formatDateTime(message.received_at || message.sent_at)}</div>
          </div>

          {/* Body */}
          <div ref={bodyRef} className="mt-2">
            {isHtml ? (
              <iframe
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:Arial,sans-serif;font-size:13px;line-height:1.5;color:#333;margin:8px;background:#fff;}img{max-width:100%;height:auto;}p{margin-bottom:0.5em;}a{color:#2563eb;}${QUOTE_STYLES}</style></head><body>${displayContent}</body></html>`}
                className="w-full min-h-[100px] border-0 bg-white rounded"
                title={`Message from ${message.from}`}
                sandbox="allow-same-origin"
                onLoad={(e) => {
                  const iframe = e.target;
                  if (iframe.contentDocument) {
                    iframe.style.height = Math.min(iframe.contentDocument.body.scrollHeight + 20, 500) + 'px';
                  }
                }}
              />
            ) : (
              <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans leading-relaxed bg-slate-800/30 rounded p-3 max-h-[400px] overflow-y-auto">
                {displayContent}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ThreadView = ({ threadId, currentEmailId }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    const fetchThread = async () => {
      setLoading(true);
      try {
        const data = await getThreadEmails(threadId);
        if (!cancelled) {
          setMessages(data.emails || []);
          // Auto-expand current email and last message
          const expanded = new Set();
          if (currentEmailId) expanded.add(currentEmailId);
          if (data.emails?.length > 0) {
            expanded.add(data.emails[data.emails.length - 1].id);
          }
          setExpandedIds(expanded);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load thread');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchThread();
    return () => { cancelled = true; };
  }, [threadId, currentEmailId]);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedIds(new Set(messages.map(m => m.id)));
  const collapseAll = () => setExpandedIds(new Set([currentEmailId]));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
        <span className="ml-2 text-sm text-slate-400">Loading conversation...</span>
      </div>
    );
  }

  if (error || messages.length <= 1) return null;

  return (
    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden">
      {/* Thread header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium text-cyan-300">Conversation</span>
          <span className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded-full text-xs">{messages.length} messages</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button onClick={expandAll} className="text-slate-400 hover:text-white transition-colors">Expand all</button>
          <span className="text-slate-600">|</span>
          <button onClick={collapseAll} className="text-slate-400 hover:text-white transition-colors">Collapse</button>
        </div>
      </div>

      {/* Messages */}
      <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
        {messages.map((message) => (
          <ThreadMessage
            key={message.id}
            message={message}
            isCurrentEmail={message.id === currentEmailId}
            isExpanded={expandedIds.has(message.id)}
            onToggle={() => toggleExpand(message.id)}
            isInThread={true}
          />
        ))}
      </div>
    </div>
  );
};

export default ThreadView;
